import { Alert, Anchor, Badge, Card, Group, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { formatMoney } from '../../lib/format';
import type { ReferralBalance } from './PayPartnerButton';
import { PROFILES_PATH, type ReferralPartner } from './partner';

/**
 * Who is carrying over, how far short they are, and how to reach them.
 *
 * ## What this deliberately is not
 *
 * The requirement asks for a ONE-TAP MESSAGE to a partner under their minimum, and this system has
 * no way to send a message: no WhatsApp Business account, no SMS gateway, no mail sender, and no
 * setting anywhere that names one. Building a "Send" button on top of that means picking the
 * channel, the number it comes from and who pays for it — decisions that belong to the owner and
 * not to whoever happened to write this screen. A button that looked like it sent something and
 * quietly did not would be the worst of the three options.
 *
 * So this is the half that needs no decision: KNOWING who is under and by how much, on one screen,
 * with the driver's own WhatsApp and phone beside the figure. The owner taps the number, his own
 * WhatsApp opens on the conversation, and he says it in his own words and his own Spanish — which
 * is what he does today anyway, minus the part where he has to work out who to say it to.
 *
 * ## Why it is worth a section of its own
 *
 * Below-minimum partners are already in "What we owe", each with an orange note beside them. That
 * is a table sorted by nothing in particular, and the question here is a different one: not "what
 * do I owe" but "who is close, and who has been stuck for weeks". A driver silently carrying over
 * is the one who stops bringing guests, and nothing on the old screen made him easier to find than
 * anybody else.
 */

const BALANCES_PATH = '/admin/v1/referrals/balances';

/** Digits only, which is what a `wa.me` link takes and what a phone dialler will accept. */
function dialable(value: string | null | undefined): string | null {
  const digits = (value ?? '').replace(/[^\d]/g, '');
  return digits.length >= 8 ? digits : null;
}

export function UnderTheirMinimum() {
  const balances = useQuery({
    queryKey: listKey(BALANCES_PATH),
    queryFn: () => rawApi<ReferralBalance[]>('GET', BALANCES_PATH),
  });
  const partners = useQuery({
    queryKey: listKey(PROFILES_PATH),
    queryFn: () => rawApi<ReferralPartner[]>('GET', PROFILES_PATH),
  });
  const byId = new Map((partners.data ?? []).map((p) => [p.id, p]));

  // Furthest from their minimum first: the partner who has been carrying over longest is the one
  // worth a word, and the one three lempiras short will clear it on his next table.
  const short = (balances.data ?? [])
    .filter((b) => !b.payable && typeof b.shortfallMinor === 'number' && b.shortfallMinor > 0)
    .sort((a, b) => (b.shortfallMinor ?? 0) - (a.shortfallMinor ?? 0));

  if (balances.isLoading || short.length === 0) return null;

  return (
    <Card withBorder padding="lg" mb="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={5}>Not yet at their minimum</Title>
            <Text size="sm" c="dimmed">
              Commission is building up for these partners but has not reached the minimum you set for them,
              so it carries over. Cash can still be handed over any time.
            </Text>
          </div>
          <Badge variant="light" color="orange">
            {short.length} {short.length === 1 ? 'partner' : 'partners'}
          </Badge>
        </Group>

        <Table.ScrollContainer minWidth={620}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Partner</Table.Th>
                <Table.Th ta="right">Built up so far</Table.Th>
                <Table.Th ta="right">Still to go</Table.Th>
                <Table.Th>Tell them</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {short.map((b) => {
                const partner = byId.get(b.profileId);
                const whatsapp = dialable(partner?.whatsapp);
                const phone = dialable(partner?.phone);
                return (
                  <Table.Tr key={`${b.profileId}|${b.currency}`}>
                    <Table.Td>
                      <Anchor component={Link} to={`/referrals/partners/${b.profileId}`} fw={600}>
                        {b.profileName}
                      </Anchor>
                    </Table.Td>
                    <Table.Td ta="right">{formatMoney(b.accruedMinor, b.currency)}</Table.Td>
                    <Table.Td ta="right" c="orange" fw={600}>
                      {formatMoney(b.shortfallMinor as number, b.currency)}
                    </Table.Td>
                    <Table.Td>
                      {/* Opens the owner's own WhatsApp on the conversation. Nothing is sent from
                          here — he writes it and presses send, in his own words. */}
                      <Group gap="xs">
                        {whatsapp && (
                          <Anchor
                            href={`https://wa.me/${whatsapp}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                          >
                            WhatsApp
                          </Anchor>
                        )}
                        {phone && (
                          <Anchor href={`tel:${phone}`} size="sm">
                            Call
                          </Anchor>
                        )}
                        {!whatsapp && !phone && (
                          <Text size="sm" c="dimmed" fs="italic">
                            No number on file
                          </Text>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        <Alert variant="light" color="gray">
          Messages are not sent from here. Tapping a number opens your own WhatsApp or dialler, so what you
          say and which language you say it in is yours.
        </Alert>
      </Stack>
    </Card>
  );
}
